import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { statePrivacyLaws } from '../data/statePrivacyLaws';
import type { StatePrivacyLaw } from '../data/statePrivacyLaws';

/** Fixed revision date — change it whenever the policy text changes. */
const LAST_UPDATED = 'September 24, 2026';
type RightKey = keyof Pick<StatePrivacyLaw, 'access' | 'correct' | 'delete' | 'portability' | 'optOutSale' | 'optOutTargetedAds' | 'optOutProfiling' | 'appeal'>;
const rightColumns: {
  key: RightKey;
  label: string;
}[] = [{
  key: 'access',
  label: 'Access'
}, {
  key: 'correct',
  label: 'Correct'
}, {
  key: 'delete',
  label: 'Delete'
}, {
  key: 'portability',
  label: 'Portability'
}, {
  key: 'optOutSale',
  label: 'Opt out: sale'
}, {
  key: 'optOutTargetedAds',
  label: 'Opt out: ads'
}, {
  key: 'optOutProfiling',
  label: 'Opt out: profiling'
}, {
  key: 'appeal',
  label: 'Appeal'
}];
export function PrivacyPolicyPage() {
  usePageMeta('Privacy Policy | FindOnlineColleges', 'Learn how FindOnlineColleges.org collects, uses, and protects your personal information.');
  return <main className="bg-white py-10 sm:py-16" data-id="element-2209" data-mp="2jq2n3ri019gd">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-2210" data-mp="2jnq4dc4f2v29">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-2211" data-mp="2er979sf8p6d1">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2212" data-mp="1m2kbm8wydt8o" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-2213" data-mp="20s00fjcxbolq">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-2214" data-mp="1w0i1t1ydi03x">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3" data-id="element-2215" data-mp="189cl6kklz3sk">
            Last updated: {LAST_UPDATED}
          </p>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base" data-id="element-2216" data-mp="1szokdw7qc9sc">
          <p data-id="element-2217" data-mp="1sz6q354mo5n2">
            This privacy notice for FindOnlineColleges.org describes how and why
            we might collect, store, use, and/or share your information when you
            use our services, such as when you:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2218" data-mp="26r3f30b01zf8">
            <li data-id="element-2219" data-mp="26ps34d7ehld1">
              Visit our website, or any website of ours that links to this
              privacy notice
            </li>
            <li data-id="element-2220" data-mp="1a36xbs3tsy2f">
              Engage with us in other related ways, including any sales,
              marketing, or events
            </li>
          </ul>
          <p data-id="element-2221" data-mp="1j0qzr4dfd0em">
            <strong className="text-navy" data-id="element-2222" data-mp="1izxs816n66vf">Questions or concerns?</strong>{' '}
            Reading this privacy notice will help you understand your privacy
            rights and choices. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </div>

        {/* Summary of Key Points */}
        <Section title="Summary of Key Points" data-id="element-2223" data-mp="nsyhate1okwf">
          <SummaryItem label="What personal information do we process?" text="When you visit, use, or navigate our services, we may process personal information depending on how you interact with us, the choices you make, and the features you use." data-id="element-2224" data-mp="nrndwuoihf5u" />
          <SummaryItem label="Do we process sensitive personal information?" text="We do not process sensitive personal information." data-id="element-2225" data-mp="92r9d7lnyk9z" />
          <SummaryItem label="Do we receive information from third parties?" text="We may receive technical and advertising data from third parties, including advertising platforms and analytics providers." data-id="element-2226" data-mp="2b721gnpj6p" />
          <SummaryItem label="How do we process your information?" text="To provide, improve, and administer our services, communicate with you, ensure security and fraud prevention, comply with law, and with your consent where required." data-id="element-2227" data-mp="3mbvmkur3ew58" />
          <SummaryItem label="When and with whom do we share personal information?" text="Only in specific circumstances (e.g., business transfers or service providers)." data-id="element-2228" data-mp="1skf58747870n" />
          <SummaryItem label="How do we keep your information safe?" text="With organizational and technical measures, but no method is 100% secure." data-id="element-2229" data-mp="1ikei0a37l6r6" />
          <SummaryItem label="What are your rights?" text="All U.S. consumers can access, port, correct, and delete their personal information, opt out of sale, sharing, targeted advertising, and profiling, and appeal our decisions. Residents of states with privacy laws have these rights by law (see Sections 10 and 11)." data-id="element-2230" data-mp="19ruplrdifyes" />
          <SummaryItem label="How do you exercise your rights?" text="Use our online privacy request form, email contact@findonlinecolleges.org, write to us, or turn on Global Privacy Control in your browser (see Section 9)." data-id="element-2231" data-mp="qu080oybgeks" />
        </Section>

        {/* TOC */}
        <Section title="Table of Contents" data-id="element-2232" data-mp="7885t9bk479l">
          <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-2233" data-mp="799q84egq63z">
            {tocItems.map(item => <li key={item.id} data-id="element-2234" data-mp="l9kkziiw84uf">
                <a href={`#${item.id}`} className="text-navy hover:text-crimson hover:underline transition-colors" data-id="element-2235" data-mp="l5n0asgo358f">
                  {item.label}
                </a>
              </li>)}
          </ol>
        </Section>

        {/* Section 1 */}
        <NumberedSection id="section-1" number={1} title="What Information Do We Collect?" data-id="element-2236" data-mp="13yhdphyn6paj">
          <SubHeading data-id="element-2237" data-mp="zwnkfuqs0nke">Personal information you disclose to us</SubHeading>
          <Paragraph data-id="element-2238" data-mp="1dwf1k4clak2m">
            We collect personal information that you voluntarily provide to us
            when you express an interest in obtaining information about us or
            our services, when you participate in activities on the services, or
            otherwise when you contact us.
          </Paragraph>
          <Paragraph data-id="element-2239" data-mp="amsu25081pxz">
            <strong className="text-navy" data-id="element-2240" data-mp="amanaefxntbt">
              Personal Information Provided by You.
            </strong>{' '}
            The personal information we collect may include:
          </Paragraph>
          <BulletList items={['Names', 'Phone numbers', 'Email addresses', 'Mailing addresses', 'Education level and interests', 'Program preferences']} data-id="element-2241" data-mp="kd5ezyby7l4n" />

          <SubHeading data-id="element-2242" data-mp="ycesurcom35g">Information automatically collected</SubHeading>
          <Paragraph data-id="element-2243" data-mp="13a6ifeputd6h">
            We automatically collect certain information when you visit, use, or
            navigate the services. This information does not reveal your
            specific identity but may include device and usage information, such
            as:
          </Paragraph>
          <BulletList items={['IP address', 'Browser and device characteristics', 'Operating system', 'Language preferences', 'Referring URLs', 'Device name and type', 'Pages viewed and time spent', 'Clickstream data']} data-id="element-2244" data-mp="2xi0m0hepn555" />
        </NumberedSection>

        {/* Section 2 */}
        <NumberedSection id="section-2" number={2} title="How Do We Process Your Information?" data-id="element-2245" data-mp="3ghekncndx41z">
          <Paragraph data-id="element-2246" data-mp="3biuuf01rd63t">
            We process your personal information for a variety of reasons,
            including:
          </Paragraph>
          <BulletList items={['To facilitate account creation and authentication', 'To deliver and facilitate delivery of services to you', 'To respond to user inquiries and offer support', 'To send administrative information to you', 'To fulfill and manage your requests', 'To enable user-to-user communications', 'To request feedback and contact you about your use of our services', 'To send you marketing and promotional communications', 'To deliver targeted advertising to you', 'To protect our services and investigate potential violations', 'To identify usage trends and determine the effectiveness of campaigns', "To save or protect an individual's vital interest", 'To comply with our legal obligations']} data-id="element-2247" data-mp="32hn7tf8lttpk" />

          <SubHeading data-id="element-2248" data-mp="3yyu6zixiejx">Email Communications</SubHeading>
          <Paragraph data-id="element-2249" data-mp="8yl5v4j3zmqm">
            When you submit a form on our website, you may consent to receive
            marketing and promotional emails from FindOnlineColleges.org and its
            educational partners at the email address you provide. Marketing
            emails are sent from{' '}
            <span className="font-medium text-navy" data-id="element-2250" data-mp="1atxuonfj2ocf">
              contact@findonlinecolleges.org
            </span>
            . Every marketing email includes an unsubscribe link. You may opt
            out at any time and your request will be honored within 10 business
            days. Opting out of marketing email does not affect transactional or
            administrative messages related to a specific inquiry you have
            submitted.
          </Paragraph>
        </NumberedSection>

        {/* Section 3 */}
        <NumberedSection id="section-3" number={3} title="SMS / Text Message Communications" data-id="element-2251" data-mp="nu9woydkgx9p">
          <SubHeading data-id="element-2252" data-mp="11ukmrz71y7tt">How We Collect SMS Consent</SubHeading>
          <Paragraph data-id="element-2253" data-mp="wtww5emqjt04">
            When you submit a form on our website, you may expressly consent to
            receive autodialed and/or pre-recorded marketing text messages
            (SMS/MMS) from FindOnlineColleges.org and its educational partners at
            the mobile phone number you provide. This consent is obtained at the
            point of form submission and is documented with the date, time, IP
            address, and page URL at which consent was given.
          </Paragraph>

          <SubHeading data-id="element-2254" data-mp="16su5pf245l1g">How We Use Your Phone Number for SMS</SubHeading>
          <Paragraph data-id="element-2255" data-mp="11uskanh7t9mt">
            If you have provided consent, we may send you text messages for
            purposes including:
          </Paragraph>
          <BulletList items={['Information about educational programs matching your interests', 'Follow-up communications related to your inquiry', 'Promotional offers and marketing messages from us or our educational partners']} data-id="element-2256" data-mp="xuifvf92mra2" />

          <SubHeading data-id="element-2257" data-mp="ixhl12vl2g7d">Consent Is Not Required</SubHeading>
          <Paragraph data-id="element-2258" data-mp="2i90x3qc6o4l9">
            Your consent to receive SMS/MMS messages is never a condition of
            using our services or receiving information. You may choose not to
            provide consent and still access our website and resources.
          </Paragraph>

          <SubHeading data-id="element-2259" data-mp="2psa1f55u1d3g">Message Frequency and Rates</SubHeading>
          <Paragraph data-id="element-2260" data-mp="2pqgx3wioz4ym">
            Message frequency varies based on your interactions and preferences.
            Standard message and data rates may apply depending on your carrier
            and plan.
          </Paragraph>

          <SubHeading data-id="element-2261" data-mp="22ru53xiw3a6r">How to Opt Out</SubHeading>
          <Paragraph data-id="element-2262" data-mp="22t5k6thsc4d6">
            You may revoke your consent and opt out of SMS communications at any
            time by:
          </Paragraph>
          <BulletList items={['Replying STOP to any text message you receive from us', 'Contacting us directly using the information in Section 14']} data-id="element-2263" data-mp="26v8uvfwmr4xe" />
          <Paragraph data-id="element-2264" data-mp="3n7xpgmmdterf">
            Opt-out requests will be honored promptly. After opting out, you may
            receive a single confirmation message. You will not receive further
            SMS marketing messages unless you provide new consent.
          </Paragraph>

          <SubHeading data-id="element-2265" data-mp="32kg1cbwdwh8e">For Assistance</SubHeading>
          <Paragraph data-id="element-2266" data-mp="2xiiaavhqsi4w">
            Reply HELP to any text message for support information, or contact
            us directly using the information in Section 14.
          </Paragraph>

          <SubHeading data-id="element-2267" data-mp="37h6il2jxdive">Third-Party SMS Senders</SubHeading>
          <Paragraph data-id="element-2268" data-mp="37fmy0zr8klnt">
            When you consent to receive communications from our educational
            partners, those partners may contact you directly via SMS under
            their own messaging programs and privacy policies. We encourage you
            to review the privacy policies of any educational institution or
            partner that contacts you.
          </Paragraph>

          <SubHeading data-id="element-2269" data-mp="2opx2gafnfvuu">Record Retention</SubHeading>
          <Paragraph data-id="element-2270" data-mp="210ueylyc38sf">
            We retain records of SMS consent, including timestamp, IP address,
            form URL, and submitted form data, in accordance with our general
            data retention practices described in Section 6.
          </Paragraph>
        </NumberedSection>

        {/* Section 4 */}
        <NumberedSection id="section-4" number={4} title="When and With Whom Do We Share Your Personal Information?" data-id="element-2271" data-mp="gca4gmab1v6z">
          <Paragraph data-id="element-2272" data-mp="6lf8gq79bh8c">
            We may share information in the following situations:
          </Paragraph>
          <ul className="space-y-3 mt-4" data-id="element-2273" data-mp="ub9lb7rkt5sl">
            {[{
            label: 'Business Transfers',
            text: 'In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition.'
          }, {
            label: 'Educational Institutions',
            text: 'We share your information with schools and educational partners that match your interests.'
          }, {
            label: 'Service Providers',
            text: 'With third-party vendors, service providers, contractors, or agents who perform services for us.'
          }, {
            label: 'Advertising Partners',
            text: 'With advertising partners to deliver targeted ads.'
          }, {
            label: 'Legal Requirements',
            text: 'When required by law or to protect rights, safety, and property.'
          }].map(item => <li key={item.label} className="flex gap-3 leading-relaxed text-gray-700" data-id="element-2274" data-mp="d1pdrgis78aw">
                <span className="text-navy mt-2 w-1.5 h-1.5 rounded-full bg-navy flex-shrink-0" data-id="element-2275" data-mp="81kwvpdyobk5" />
                <span data-id="element-2276" data-mp="36nq6p45meaf">
                  <strong className="text-navy" data-id="element-2277" data-mp="3v8dhp323hpez">{item.label}:</strong>{' '}
                  {item.text}
                </span>
              </li>)}
          </ul>
        </NumberedSection>

        {/* Section 5 */}
        <NumberedSection id="section-5" number={5} title="Do We Use Cookies and Other Tracking Technologies?" data-id="element-2278" data-mp="2jsymo4tg57mi">
          <Paragraph data-id="element-2279" data-mp="z92yfso0gg8p">
            Yes, we use cookies and similar tracking technologies to access or
            store information. See our Cookie Notice for more details about how
            we use cookies.
          </Paragraph>
        </NumberedSection>

        {/* Section 6 */}
        <NumberedSection id="section-6" number={6} title="How Long Do We Keep Your Information?" data-id="element-2280" data-mp="1d45akxjgb1ty">
          <Paragraph data-id="element-2281" data-mp="1n0qgrm77iizd">
            We keep your information for as long as necessary to fulfill the
            purposes outlined in this privacy notice unless otherwise required
            by law. When we have no ongoing legitimate business need to process
            your personal information, we will delete or anonymize it.
          </Paragraph>
        </NumberedSection>

        {/* Section 7 */}
        <NumberedSection id="section-7" number={7} title="How Do We Keep Your Information Safe?" data-id="element-2282" data-mp="1n5l09ohn13m">
          <Paragraph data-id="element-2283" data-mp="14zcpokq7lmzo">
            We have implemented appropriate technical and organizational
            security measures designed to protect the security of any personal
            information we process. However, no electronic transmission over the
            internet or information storage technology can be guaranteed to be
            100% secure.
          </Paragraph>
        </NumberedSection>

        {/* Section 8 */}
        <NumberedSection id="section-8" number={8} title="Do We Collect Information From Minors?" data-id="element-2284" data-mp="1nv2qw5qbifci">
          <Paragraph data-id="element-2285" data-mp="1wq0mxlori882">
            We do not knowingly solicit data from or market to children under 18
            years of age. If you become aware that a child has provided us with
            personal information, please contact us. If we learn we have
            collected personal information from a child under 18, we will delete
            that information.
          </Paragraph>
        </NumberedSection>

        {/* Section 9 */}
        <NumberedSection id="section-9" number={9} title="Your Privacy Rights and How to Exercise Them" data-id="element-2286" data-mp="2lawxjpqmg98j">
          <Paragraph data-id="element-2287" data-mp="2bcg4mn84hkju">
            A growing number of U.S. states give their residents privacy rights
            over personal information. The states and the rights each law grants
            are listed in Section 11. To keep things simple and fair,{' '}
            <strong className="text-navy" data-id="element-2288" data-mp="22ikybbv9lo2m">
              we extend the rights below to every U.S. consumer
            </strong>
            , whether or not your state has passed a privacy law yet.
          </Paragraph>

          <SubHeading data-id="element-2289" data-mp="1sjve3cfwbrj7">Your Rights</SubHeading>
          <BulletList items={['Right to know and access: Confirm whether we process your personal information, and get the categories and specific pieces we hold, the sources, the purposes, and the categories of third parties we disclose it to.', 'Right to portability: Get a copy of your personal information in a portable, readily usable format.', 'Right to correct: Ask us to correct inaccurate personal information.', 'Right to delete: Ask us to delete personal information we collected from you or about you, subject to exceptions allowed by law (for example, records we must keep to prove your consent to be contacted).', 'Right to opt out of sale and sharing: Tell us to stop disclosing your personal information to educational partners and advertising partners in ways that count as a "sale" or "sharing" under state law.', 'Right to opt out of targeted advertising: Tell us to stop using your personal information to show you ads based on your activity across other websites and apps.', 'Right to opt out of profiling: Tell us not to use automated processing of your personal information to make decisions that produce legal or similarly significant effects. We do not make decisions of this kind.', 'Right to a list of third parties: Where your state provides it, get a list of the categories, or the specific third parties, to which we have disclosed your personal information.', 'Right to limit use of sensitive information: We do not collect sensitive personal information, as defined by state law. If that changes, we will only process it with your consent or as allowed by law.', 'Right to appeal: If we decline to act on your request, you can appeal our decision (see "Appealing Our Decision" below).', 'Right to non-discrimination: We will not deny you services, charge you different prices, or give you a different level of service because you exercised any of these rights.']} data-id="element-2290" data-mp="1xjzul2gt0m9f" />

          <SubHeading data-id="element-2291" data-mp="26nc6g23qjgnb">How to Exercise Your Rights</SubHeading>
          <Paragraph data-id="element-2292" data-mp="26kpyi1cxmz4k">You can submit a request in any of these ways:</Paragraph>
          <ol className="list-decimal pl-6 space-y-3 marker:text-crimson marker:font-bold" data-id="element-2293" data-mp="2bjsuyxeujx0x">
            <li className="text-gray-700 leading-relaxed" data-id="element-2294" data-mp="1jiodw6a7vu8z">
              <strong className="text-navy" data-id="element-2295" data-mp="1ohrjbru402hh">Online:</strong> Complete our{' '}
              <Link to="/do-not-sell-my-info" className="text-navy underline hover:text-crimson" data-id="element-2296" data-mp="1thdxq4loborv">
                privacy request form
              </Link>{' '}
              and choose the type of request. The form handles opt-outs,
              access, portability, correction, deletion, third-party lists,
              marketing opt-outs, and appeals.
            </li>
            <li className="text-gray-700 leading-relaxed" data-id="element-2297" data-mp="1aqctp25p531g">
              <strong className="text-navy" data-id="element-2298" data-mp="1aotd1ag4opz5">By email:</strong> Write to{' '}
              <a href="mailto:contact@findonlinecolleges.org?subject=Privacy%20Rights%20Request" className="text-navy underline hover:text-crimson" data-id="element-2299" data-mp="1fj5ujey1nr89">
                contact@findonlinecolleges.org
              </a>{' '}
              with the subject line{' '}
              <strong className="text-navy" data-id="element-2300" data-mp="30as8cg6wl3su">"Privacy Rights Request."</strong>{' '}
              Include your name, the email address and phone number you gave
              us, your state of residence, and the right you want to exercise.
            </li>
            <li className="text-gray-700 leading-relaxed" data-id="element-2301" data-mp="2mcljfpjxme5b">
              <strong className="text-navy" data-id="element-2302" data-mp="2me6dzf68xx36">By mail:</strong> Birddog Media,
              Attn: Privacy Requests, 1226 W. Northwest Highway, Palatine, IL
              60067.
            </li>
            <li className="text-gray-700 leading-relaxed" data-id="element-2303" data-mp="2cjw9iz4158c3">
              <strong className="text-navy" data-id="element-2304" data-mp="28lgq1575qi3f">By browser signal:</strong> If your
              browser or extension sends a Global Privacy Control (GPC) signal,
              we treat it as a request to opt out of sale, sharing, and targeted
              advertising for that browser (see Section 12).
            </li>
          </ol>
          <Paragraph data-id="element-2305" data-mp="1tnwoy9ddt72j">
            To stop marketing emails, click the unsubscribe link in any email.
            To stop text messages, reply <strong className="text-navy" data-id="element-2306" data-mp="1oylulazlrw95">STOP</strong>{' '}
            to any message. Marketing opt-outs are processed within 10 business
            days.
          </Paragraph>

          <SubHeading data-id="element-2307" data-mp="1b0x9q35jcp43">Verifying Your Request</SubHeading>
          <Paragraph data-id="element-2308" data-mp="1f1h7n8xxnwra">
            To protect your information, we verify requests to access, port,
            correct, or delete it. We do this by matching details you give us
            (such as your name, email address, and phone number) against the
            information we hold, and we may ask you to confirm the request from
            that email address. We only use the information you give us for
            verification to verify and respond to your request. You do not need
            to verify your identity to opt out of sale, sharing, targeted
            advertising, or profiling. If we cannot verify a deletion request,
            we will still treat it as an opt-out request.
          </Paragraph>

          <SubHeading data-id="element-2309" data-mp="eysz43cvn3ym">Authorized Agents</SubHeading>
          <Paragraph data-id="element-2310" data-mp="a22ldmojokgd">
            You may use an authorized agent to submit a request for you. We will
            ask the agent for your signed written permission, or a valid power
            of attorney. For requests other than opt-outs, we may also ask you
            to verify your identity directly with us. Parents or legal guardians
            may submit requests for a child.
          </Paragraph>

          <SubHeading data-id="element-2311" data-mp="3n8ukx8v9184d">Response Timing and Cost</SubHeading>
          <Paragraph data-id="element-2312" data-mp="3nafeav5tul0n">
            We confirm receipt of requests within 10 business days. We respond
            within 45 days of receiving your request. Where the law allows, we
            may extend this once by up to another 45 days when reasonably
            necessary, and we will tell you why within the first 45 days. Opt-out
            requests are processed as soon as possible, and no later than 15
            business days after we receive them. Requests are free. Where the
            law allows, we may charge a reasonable fee for, or decline, requests
            that are manifestly unfounded, excessive, or repetitive, and we will
            explain why.
          </Paragraph>

          <SubHeading data-id="element-2313" data-mp="3r7b996pd7vi7">Appealing Our Decision</SubHeading>
          <Paragraph data-id="element-2314" data-mp="3w4uoo9xsuebq">
            If we decline to act on all or part of your request, we will explain
            why. You may appeal by using the privacy request form and choosing
            "Appeal a decision," or by emailing{' '}
            <a href="mailto:contact@findonlinecolleges.org?subject=Privacy%20Request%20Appeal" className="text-navy underline hover:text-crimson" data-id="element-2315" data-mp="9uics3lxyzlv">
              contact@findonlinecolleges.org
            </a>{' '}
            with the subject line{' '}
            <strong className="text-navy" data-id="element-2316" data-mp="nm5cyej08s9x">"Privacy Request Appeal"</strong>{' '}
            within a reasonable time after our decision. We will respond in
            writing within the time your state's law requires (generally 45 to
            60 days), and explain the action we took or did not take and why. If
            we deny your appeal, we will tell you how to contact your state
            Attorney General to submit a complaint.
          </Paragraph>

          <SubHeading data-id="element-2317" data-mp="ostjckdg1vy9">What Opting Out Means for Your School Matches</SubHeading>
          <Paragraph data-id="element-2318" data-mp="1li0xh7bqh2s5">
            When you ask us to connect you with specific schools, we share your
            information with those schools at your direction. Opting out stops
            future sale and sharing, and stops targeted advertising based on
            your information. It does not undo information already delivered to
            schools you asked to hear from, but we will tell those schools about
            your request where the law requires it. To stop being contacted by
            a particular school, you can also contact that school directly.
          </Paragraph>
        </NumberedSection>

        {/* Section 10 */}
        <NumberedSection id="section-10" number={10} title="Your California Privacy Rights (CCPA/CPRA)" data-id="element-2319" data-mp="3trh4a19eupep">
          <Paragraph data-id="element-2320" data-mp="3k0cqh4vh1alg">
            This section applies only to California residents. Under the
            California Consumer Privacy Act (CCPA), as amended by the California
            Privacy Rights Act (CPRA), you have specific rights regarding your
            personal information.
          </Paragraph>

          <SubHeading data-id="element-2321" data-mp="3fz0w6m1nk16x">Categories of Personal Information Collected</SubHeading>
          <Paragraph data-id="element-2322" data-mp="3118i7ybr7kuu">
            In the preceding 12 months, we have collected the following
            categories of personal information:
          </Paragraph>
          <BulletList items={['Identifiers (e.g., name, email address, phone number, IP address)', 'Personal information categories listed in the California Customer Records statute (e.g., contact information)', 'Internet or other similar network activity (e.g., browsing history, interaction with our website)', 'Education and program-interest information (e.g., degree level, area of study)', 'Inferences drawn from other personal information (e.g., preferences and characteristics)']} data-id="element-2323" data-mp="361wbkqnggds8" />

          <SubHeading data-id="element-2324" data-mp="2tnhi8453xahq">How We Use and Share Your Information</SubHeading>
          <Paragraph data-id="element-2325" data-mp="37fe3bo8cyk4m">
            We use this information to provide our services, match you with
            educational programs, and for marketing purposes. We may share this
            information with the following categories of third parties:
          </Paragraph>
          <BulletList items={['Educational partners (to fulfill your requests for information)', 'Advertising partners (to deliver targeted advertisements)', 'Service providers (to operate our website and services)']} data-id="element-2326" data-mp="3cfsf9hd2ye7x" />

          <SubHeading data-id="element-2327" data-mp="w9n2ed51xxjs">Your CCPA/CPRA Rights</SubHeading>
          <BulletList items={['Right to Know/Access: You can request information about the categories and specific pieces of personal information we have collected about you.', 'Right to Delete: You can request that we delete your personal information, subject to certain exceptions.', 'Right to Correct: You can request that we correct inaccurate personal information we hold about you.', 'Right to Opt-Out: You have the right to opt out of the sale or sharing of your personal information.', 'Right to Limit Use: You have the right to limit the use and disclosure of sensitive personal information (note: we do not generally collect sensitive personal information).', 'Right to Non-Discrimination: We will not discriminate against you for exercising any of your CCPA rights.']} data-id="element-2328" data-mp="w70wsj1q1s47" />

          <SubHeading data-id="element-2329" data-mp="23o9rar04kqk7">Sale and Sharing</SubHeading>
          <Paragraph data-id="element-2330" data-mp="3o3fz929qmklq">
            We do not sell personal information for money. In the preceding 12
            months, we may have "sold" or "shared" identifiers, internet
            activity, education and program-interest information, and inferences
            to educational partners and advertising partners, as those terms are
            defined by the CCPA. We do not knowingly sell or share the personal
            information of consumers under 18.
          </Paragraph>

          <SubHeading data-id="element-2331" data-mp="30en6tfsbquvv">Retention</SubHeading>
          <Paragraph data-id="element-2332" data-mp="30gzorsy3158x">
            We keep each category of personal information for as long as
            described in Section 6.
          </Paragraph>

          <SubHeading data-id="element-2333" data-mp="2qhimlli1h9hp">How to Submit a Request</SubHeading>
          <Paragraph data-id="element-2334" data-mp="2qj22hazs9iiy">
            To opt out of sale or sharing, use our{' '}
            <Link to="/do-not-sell-my-info" className="text-navy underline hover:text-crimson" data-id="element-2335" data-mp="tnnrep1wxkup">
              Do Not Sell or Share My Personal Information
            </Link>{' '}
            page, or turn on Global Privacy Control in your browser. For every
            other request, and for our process for verification, authorized
            agents, and response times, see Section 9.
          </Paragraph>
        </NumberedSection>

        {/* Section 11 */}
        <NumberedSection id="section-11" number={11} title="Additional U.S. State Privacy Rights" data-id="element-2336" data-mp="1js2uxvmtbs6i">
          <Paragraph data-id="element-2337" data-mp="r1u99tt2ipn6">
            Residents of the states below have privacy rights under their state's
            consumer privacy law. As explained in Section 9, we honor all of
            these rights for every U.S. consumer. The table shows what each law
            requires. To exercise any right, use the methods in Section 9.
          </Paragraph>

          <div className="mt-6 -mx-4 sm:mx-0 overflow-x-auto" data-id="element-2338" data-mp="2gibtverx6zvb">
            <table className="min-w-[720px] w-full text-sm border-collapse" data-id="element-2339" data-mp="2cdlz5t8zzrbz">
              <caption className="sr-only" data-id="element-2340" data-mp="2jxn26rrjjdoo">
                Consumer privacy rights by state
              </caption>
              <thead data-id="element-2341" data-mp="2oyas1a419ohb">
                <tr className="border-b-2 border-gray-300 text-left" data-id="element-2342" data-mp="2oyub4u5f6iwx">
                  <th scope="col" className="py-3 pl-4 sm:pl-0 pr-4 font-bold text-navy" data-id="element-2343" data-mp="2a0s3a7rcvb9v">
                    State
                  </th>
                  {rightColumns.map(col => <th key={col.key} scope="col" className="py-3 px-2 font-semibold text-gray-700 text-center whitespace-nowrap" data-id="element-2344" data-mp="32o4k01au4ov9">
                      {col.label}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-2345" data-mp="2xwd1me1u1pg7">
                {statePrivacyLaws.map(law => <tr key={law.code} className="border-b border-gray-200" data-id="element-2346" data-mp="3lmh4gj775l65">
                    <th scope="row" className="py-3 pl-4 sm:pl-0 pr-4 text-left font-normal align-top" data-id="element-2347" data-mp="3qdyw2gnby9eg">
                      <span className="block font-semibold text-gray-900" data-id="element-2348" data-mp="42ql8vjsnwte">
                        {law.state}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5" data-id="element-2349" data-mp="3r8red9hgbx38">
                        {law.law} · {law.effective}
                      </span>
                    </th>
                    {rightColumns.map(col => <td key={col.key} className="py-3 px-2 text-center align-top" data-id="element-2350" data-mp="3hcxt91po2v9u">
                        {law[col.key] ? <>
                            <CheckIcon className="w-4 h-4 text-green-700 inline-block" aria-hidden="true" data-id="element-2351" data-mp="37l1v5qnkz55m" />
                            <span className="sr-only" data-id="element-2352" data-mp="2ylerszbajp9x">Yes</span>
                          </> : <>
                            <span className="text-gray-400" aria-hidden="true" data-id="element-2353" data-mp="2tmk87v5i1wor">
                              —
                            </span>
                            <span className="sr-only" data-id="element-2354" data-mp="1e2b8wpp989zl">Not required by statute</span>
                          </>}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500" data-id="element-2355" data-mp="qd8l10411kwn">
            — means the state's statute does not require that right. We still
            honor it.
          </p>

          <SubHeading data-id="element-2356" data-mp="vcuz1broj56i">State-Specific Rights and Procedures</SubHeading>
          <ul className="list-disc pl-6 space-y-2 marker:text-crimson" data-id="element-2357" data-mp="zcvmflwraqpp">
            {statePrivacyLaws.filter(law => law.note).map(law => <li key={law.code} className="text-gray-700 leading-relaxed" data-id="element-2358" data-mp="7nac6hsennry">
                  <strong className="text-navy" data-id="element-2359" data-mp="bocdr59sobx3">{law.state}:</strong> {law.note}
                </li>)}
            <li className="text-gray-700 leading-relaxed" data-id="element-2360" data-mp="3oovbo95xbmfl">
              <strong className="text-navy" data-id="element-2361" data-mp="3opeiouu1cqeu">Nevada:</strong> Under NRS Chapter
              603A, Nevada residents may tell us not to sell covered information
              they have given us. Use any method in Section 9. We respond within
              60 days.
            </li>
            <li className="text-gray-700 leading-relaxed" data-id="element-2362" data-mp="3tng43mexp1th">
              <strong className="text-navy" data-id="element-2363" data-mp="b9yqiaziy6r3">
                Colorado, Connecticut, Delaware, Maryland, Minnesota, Montana,
                Nebraska, New Hampshire, New Jersey, Oregon, and Texas:
              </strong>{' '}
              We honor universal opt-out signals such as Global Privacy Control.
            </li>
            <li className="text-gray-700 leading-relaxed" data-id="element-2364" data-mp="g91sfmk7jwuu">
              <strong className="text-navy" data-id="element-2365" data-mp="2mzrkyykvffuj">Minors:</strong> Several states
              restrict targeted advertising to, and the sale of data about,
              consumers under 18. We do not knowingly collect personal
              information from anyone under 18 (see Section 8).
            </li>
          </ul>

          <SubHeading data-id="element-2366" data-mp="12hgzgzbwldpo">If You Are Not Satisfied</SubHeading>
          <Paragraph data-id="element-2367" data-mp="12gxmlx141fii">
            After using our appeal process, you may contact your state Attorney
            General's office. California residents may also contact the
            California Privacy Protection Agency.
          </Paragraph>
        </NumberedSection>

        {/* Section 12 */}
        <NumberedSection id="section-12" number={12} title="Do-Not-Track and Global Privacy Control" data-id="element-2368" data-mp="equx9ookynnl">
          <Paragraph data-id="element-2369" data-mp="4ui2enhau3r9">
            <strong className="text-navy" data-id="element-2370" data-mp="4v06256ajk97">Global Privacy Control (GPC):</strong>{' '}
            We recognize the GPC signal. When your browser sends it, we treat it
            as a valid request to opt out of the sale and sharing of your
            personal information and of targeted advertising, and we do not load
            advertising pixels on that browser. Because we recognize the signal
            by browser, you may need to turn it on in each browser and on each
            device you use. If you are logged in or we can otherwise link the
            browser to you, we apply the opt-out to your record as well.
          </Paragraph>
          <Paragraph data-id="element-2371" data-mp="1stkegvw57s6a">
            <strong className="text-navy" data-id="element-2372" data-mp="1nt66teygek8s">Do-Not-Track (DNT):</strong> Most web
            browsers include a Do-Not-Track setting. No uniform standard for
            recognizing DNT signals has been finalized, so we do not respond to
            DNT signals that are not GPC.
          </Paragraph>
        </NumberedSection>

        {/* Section 13 */}
        <NumberedSection id="section-13" number={13} title="Do We Make Updates to This Notice?" data-id="element-2373" data-mp="2boshvftxxy93">
          <Paragraph data-id="element-2374" data-mp="346mmv3o39bfd">
            Yes, we will update this notice as necessary to stay compliant with
            relevant laws. The updated version will be indicated by an updated
            "Last updated" date at the top of this privacy notice.
          </Paragraph>
        </NumberedSection>

        {/* Section 14 */}
        <NumberedSection id="section-14" number={14} title="How Can You Contact Us About This Notice?" data-id="element-2375" data-mp="3bpnkj8c7kaut">
          <Paragraph data-id="element-2376" data-mp="3grl82ern5rtk">
            If you have questions or comments about this notice, please contact
            us at:
          </Paragraph>
          <div className="mt-5 p-6 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-2377" data-mp="3qo7tb4nvqkw4">
            <p className="font-bold text-navy text-lg" data-id="element-2378" data-mp="3up1gfvthzvny">Birddog Media</p>
            <p className="text-gray-700 mt-2" data-id="element-2379" data-mp="3un6uce6hpxia">
              1226 W. Northwest Highway, Palatine, IL 60067
            </p>
            <p className="text-gray-700 mt-2" data-id="element-2380" data-mp="89p1jfw5qn7s">
              Email:{' '}
              <a href="mailto:contact@findonlinecolleges.org" className="text-navy underline hover:text-crimson font-medium" data-id="element-2381" data-mp="8bt51wq59k7j">
                contact@findonlinecolleges.org
              </a>
            </p>
          </div>
        </NumberedSection>
      </div>
    </main>;
}
/* ---------- Sub-components ---------- */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mt-14" data-id="element-2382" data-mp="1ci81ty3679y3">
      <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-6 tracking-tight" data-id="element-2383" data-mp="1chf1beut1goe">
        {title}
      </h2>
      <div className="space-y-4" data-id="element-2384" data-mp="17ilq5t2zq1re">{children}</div>
    </section>;
}
function NumberedSection({
  id,
  number,
  title,
  children
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return <section id={id} className="mt-16 pt-10 border-t border-gray-200 scroll-mt-8" data-id="element-2385" data-mp="tl6vo9xvr3sk">
      <div className="flex items-baseline gap-3 mb-6" data-id="element-2386" data-mp="otf17oblsdx5">
        <span className="text-crimson font-bold text-xl" data-id="element-2387" data-mp="oru8834xt31e">{number}.</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight leading-tight" data-id="element-2388" data-mp="131g6hrfrfc2">
          {title}
        </h2>
      </div>
      <div className="space-y-5 leading-relaxed text-gray-700 text-base" data-id="element-2389" data-mp="3s8t4rcpkejwr">
        {children}
      </div>
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-2390" data-mp="2cl64n3gb0w29">{children}</h3>;
}
function Paragraph({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-gray-700 leading-relaxed" data-id="element-2391" data-mp="23s2tx2x1mxbb">{children}</p>;
}
function BulletList({
  items
}: {
  items: string[];
}) {
  return <ul className="list-disc pl-6 space-y-2 marker:text-crimson" data-id="element-2392" data-mp="2rhdm3w0bvjdu">
      {items.map(item => <li key={item} className="text-gray-700 leading-relaxed" data-id="element-2393" data-mp="2vhec8dwshj0q">
          {item}
        </li>)}
    </ul>;
}
function SummaryItem({
  label,
  text
}: {
  label: string;
  text: string;
}) {
  return <div className="border-l-2 border-crimson/40 pl-4 py-1" data-id="element-2394" data-mp="2mfoaae5a71op">
      <p className="text-gray-700 leading-relaxed" data-id="element-2395" data-mp="162zpfz5c067v">
        <strong className="text-navy block mb-1" data-id="element-2396" data-mp="165lyk37vets7">{label}</strong>
        {text}
      </p>
    </div>;
}
const tocItems = [{
  id: 'section-1',
  label: 'What information do we collect?'
}, {
  id: 'section-2',
  label: 'How do we process your information?'
}, {
  id: 'section-3',
  label: 'SMS / text message communications'
}, {
  id: 'section-4',
  label: 'When and with whom do we share your personal information?'
}, {
  id: 'section-5',
  label: 'Do we use cookies and other tracking technologies?'
}, {
  id: 'section-6',
  label: 'How long do we keep your information?'
}, {
  id: 'section-7',
  label: 'How do we keep your information safe?'
}, {
  id: 'section-8',
  label: 'Do we collect information from minors?'
}, {
  id: 'section-9',
  label: 'Your privacy rights and how to exercise them'
}, {
  id: 'section-10',
  label: 'Your California Privacy Rights (CCPA/CPRA)'
}, {
  id: 'section-11',
  label: 'Additional U.S. state privacy rights'
}, {
  id: 'section-12',
  label: 'Do-Not-Track and Global Privacy Control'
}, {
  id: 'section-13',
  label: 'Do we make updates to this notice?'
}, {
  id: 'section-14',
  label: 'How can you contact us about this notice?'
}];